import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { DollarSign, Users, Check, Eye } from 'lucide-react';

import { LecturerSalaryDetail } from '@/hooks/useOptimizedLecturerSalary';

interface LecturerSalaryDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lecturerName: string;
  salaryDetails: LecturerSalaryDetail[];
  onMarkAsPaid: (phaseId: string, groupId: string) => Promise<void>;
  payingPhase: string | null;
}

export const LecturerSalaryDetailsDialog = ({ 
  open, 
  onOpenChange, 
  lecturerName, 
  salaryDetails,
  onMarkAsPaid,
  payingPhase
}: LecturerSalaryDetailsDialogProps) => {
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('hy-AM', {
      style: 'currency',
      currency: 'AMD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const totalExpectedSalary = salaryDetails
    .reduce((sum, detail) => sum + detail.expected_salary, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto border-border/70 bg-card/80">
        <DialogHeader>
          <DialogTitle className="text-muted-foreground text-xl flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            {lecturerName} - Աշխատավարձի մանրամասներ
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Total Expected Salary */}
          <div className="bg-green-900/20 border border-border/70 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-lg">Ընդհանուր ակնկալվող աշխատավարձ:</span>
              <span className="text-primary text-2xl font-bold">
                {formatAmount(totalExpectedSalary)}
              </span>
            </div>
          </div>

          {/* Group By Groups */}
          {(() => {
            // Group salary details by group
            const groupedDetails = salaryDetails.reduce((acc, detail) => {
              if (!acc[detail.group_id]) {
                acc[detail.group_id] = {
                  group_name: detail.group_name,
                  student_count: detail.student_count,
                  phases: []
                };
              }
              acc[detail.group_id].phases.push(detail);
              return acc;
            }, {} as { [groupId: string]: { group_name: string; student_count: number; phases: typeof salaryDetails } });

            return Object.entries(groupedDetails).map(([groupId, groupData]) => (
              <div key={groupId} className="space-y-4">
                 {/* Group Header */}
                <div className="bg-blue-900/20 border border-blue-400/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-blue-300 text-lg font-semibold">
                        {groupData.group_name}
                      </h3>
                      <Badge variant="secondary" className="font-mono">
                        <Users className="h-3 w-3 mr-1" />
                        {groupData.student_count} ուսանող
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Phases Table for this Group */}
                <div className="rounded-md border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead className="text-muted-foreground">Փուլ</TableHead>
                        <TableHead className="text-muted-foreground">Լրացված դասեր</TableHead>
                        <TableHead className="text-muted-foreground">Աշխատավարձ</TableHead>
                        <TableHead className="text-muted-foreground">Ամսաթիվ</TableHead>
                        <TableHead className="text-muted-foreground">Փուլի ավարտ</TableHead>
                        <TableHead className="text-muted-foreground">Կարգավիճակ</TableHead>
                        <TableHead className="text-muted-foreground">Գործողություն</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupData.phases
                        .sort((a, b) => {
                          // Extract phase numbers from phase names for sorting
                          const getPhaseNumber = (phaseName: string): number => {
                            const match = phaseName.match(/փուլ\s*(\d+)/i) || phaseName.match(/phase\s*(\d+)/i);
                            return match ? parseInt(match[1]) : 999; // Put unmatched phases at the end
                          };
                          
                          const phaseNumA = getPhaseNumber(a.phase_name);
                          const phaseNumB = getPhaseNumber(b.phase_name);
                          
                          // Primary sort by phase number
                          if (phaseNumA !== phaseNumB) {
                            return phaseNumA - phaseNumB;
                          }
                          
                          // Secondary sort by last lesson date if phase numbers are equal
                          if (a.last_lesson_date && b.last_lesson_date) {
                            return new Date(a.last_lesson_date).getTime() - new Date(b.last_lesson_date).getTime();
                          }
                          
                          return 0;
                        })
                        .map((detail) => (
                        <TableRow key={`${detail.phase_id}-${detail.group_id}`} className="border-border">
                          <TableCell className="text-muted-foreground">
                            {detail.phase_name}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={`${
                                detail.lessons_taught >= 12 
                                  ? 'text-blue-400 border-blue-400/30' 
                                  : 'text-orange-400 border-orange-400/30'
                              }`}
                            >
                              {detail.lessons_taught} դաս
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-semibold text-primary">
                              {formatAmount(detail.expected_salary)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {detail.last_lesson_date ? (
                              <span className="text-muted-foreground">
                                {detail.last_lesson_date}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {detail.phase_completed ? (
                              <Badge variant="default" className="bg-green-500">
                                Ավարտված
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="font-mono">
                                Ընթացիկ
                              </Badge>
                            )}
                            {detail.twelfth_lesson_date && (
                              <div className="text-xs text-muted-foreground mt-1">
                                12-րդ դաս: {detail.twelfth_lesson_date}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={detail.payment_status === 'paid' ? 'default' : 
                                      detail.payment_status === 'payable' ? 'secondary' : 'outline'}
                              className={`${
                                detail.payment_status === 'paid' ? 'bg-primary text-primary-foreground' :
                                detail.payment_status === 'payable' ? 'bg-blue-600 text-primary-foreground' :
                                detail.payment_status === 'pending' ? 'bg-yellow-600 text-primary-foreground' :
                                'bg-muted text-primary-foreground'
                              }`}
                            >
                              {detail.payment_status === 'paid' ? (
                                <>
                                  <Check className="h-3 w-3 mr-1" />
                                  Վճարված
                                </>
                              ) : detail.payment_status === 'payable' ? 'Վճարման ենթակա' :
                                 detail.payment_status === 'pending' ? 'Սպասելու ռեժիմում' : 
                                 'Չի ստեղծվել'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {(detail.payment_status === 'payable' || detail.payment_status === 'pending') && detail.pending_salary > 0 ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={payingPhase === `${detail.phase_id}-${detail.group_id}`}
                                onClick={() => onMarkAsPaid(detail.phase_id, detail.group_id)}
                                className="border-border/70 text-muted-foreground hover:bg-green-400/10"
                              >
                                {payingPhase === `${detail.phase_id}-${detail.group_id}` ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-500/30 mr-1"></div>
                                    Վճարում...
                                  </>
                                ) : (
                                  'Նշանակել վճարված'
                                )}
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                {detail.payment_status === 'paid' ? 'Արդեն վճարված' : 
                                 detail.payment_status === 'not_generated' ? 'Դեռ չի ստեղծվել' : 
                                 'Չկա վճարելի գումար'}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ));
          })()}

          {salaryDetails.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Աշխատավարձի տվյալներ չկան
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};